import React, { FC, ReactNode, RefObject, createContext, useContext, useRef } from 'react'
import { motion } from 'framer-motion'

import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { AnimatedRouteConfig, AnimatedRouteDirection } from '@interfaces/animations'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { Stack } from '@mui/material'

const containerRefContext = createContext<RefObject<HTMLDivElement>>({
  current: null,
})
export const useContainerRef = () => useContext(containerRefContext)

export const animatedRouteValues: Record<AnimatedRouteDirection, AnimatedRouteConfig> = {
  base: {
    initial: {
      opacity: 0,
      y: 0,
    },
    end: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: -16,
    },
  },
  right: {
    initial: {
      opacity: 0,
      x: 16,
    },
    end: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: -16,
    },
  },
  left: {
    initial: {
      opacity: 0,
      x: -16,
    },
    end: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: 16,
    },
  },
  up: {
    initial: {
      opacity: 0,
      y: 16,
    },
    end: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: -16,
    },
  },
  upRight: {
    initial: {
      opacity: 0,
      x: 0,
      y: 16,
    },
    end: {
      opacity: 1,
      x: 0,
      y: 0,
    },
    exit: {
      opacity: 0,
      x: -16,
      y: 0,
    },
  },
  down: {
    initial: {
      opacity: 0,
      y: -16,
    },
    end: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: 16,
    },
  },
  deceleratedShort: {
    initial: {
      opacity: 0,
      scale: 1.1,
    },
    end: {
      opacity: 1,
      scale: 1,
    },
    exit: {
      opacity: 0,
    },
  },
  emphasizedShort: {
    initial: {
      opacity: 0,
    },
    end: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  },
}

export const animatedRouteTransitionConfig = {
  base: {
    type: 'spring',
    stiffness: 1111,
    damping: 50,
    mass: 1,
  },
  right: {
    type: 'spring',
    stiffness: 1111,
    damping: 50,
    mass: 1,
  },
  left: {
    type: 'spring',
    stiffness: 1111,
    damping: 50,
    mass: 1,
  },
  up: {
    type: 'spring',
    stiffness: 1111,
    damping: 50,
    mass: 1,
  },
  upRight: {
    type: 'spring',
    stiffness: 1111,
    damping: 50,
    mass: 1,
  },
  down: {
    type: 'spring',
    stiffness: 1111,
    damping: 50,
    mass: 1,
  },
  deceleratedShort: {
    ease: [0, 0, 0, 1],
    duration: 0.18,
  },
  emphasizedShort: {
    ease: [0.2, 0, 0, 1],
    duration: 0.2,
  },
}

interface Props {
  animationDirection: AnimatedRouteDirection
  children: ReactNode
  title?: string
  navbar?: boolean
}

const AnimatedRoute: FC<Props> = ({ children, animationDirection, title, navbar = true }) => {
  const { state } = useLocation()

  const navigationType = useNavigationType()
  const isBack = (navigationType === 'POP' && state?.isBack !== false) || state?.isBack

  const { initial, end, exit } = animatedRouteValues[animationDirection]
  const transition = animatedRouteTransitionConfig[animationDirection]
  const navigate = useNavigate()

  const containerRef = useRef<HTMLDivElement>(null)

  const content = (
    <containerRefContext.Provider value={containerRef}>
      <motion.div
        ref={containerRef}
        initial={isBack ? exit : initial}
        animate={end}
        exit={isBack ? initial : exit}
        transition={transition}
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: '100%',
        }}
      >
        {navbar && (
          <Stack pt={2} pb={1}>
            <Titlebar
              title={title}
              onActionClick={{
                type: animationDirection === 'up' ? 'close' : 'back',
                action: () => navigate(-1),
              }}
            />
          </Stack>
        )}
        {children}
      </motion.div>
    </containerRefContext.Provider>
  )

  return content
}

export default AnimatedRoute
